import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {AppFlags} from '../../../app/app-types';
import {connectAppEnv} from '../../../app/app-env';
import {connectModuleEnv} from '../../oe-module-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import {OEIconButton} from '../../elements/controls/oe-button';
import {oeUniqueIdGen} from '../../../lib/tools/oe-unique-id-gen';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEPopover from '../../views/oe-popover';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OENoteStateProvider from './elements/oe-note-state-provider';
import OENoteTextEdit from './elements/oe-note-text-edit';
import OENoteToolbar from './elements/oe-note-toolbar';
import retardUpdate from '../../../lib/react/update-retarder';

export class OENotePopoverController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.uid = oeUniqueIdGen.get();
        this.popoverTargetId = 'popover-target-id-' + this.uid;

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.id = null;
        this.isOpen = false;
        this.rect = null;

        this.unitSize = 16;
        this.pinned = true;
        
        this.state = {
            id: this.id,
            isOpen: this.isOpen,
            rect: this.rect,
            noteState: OENoteStateProvider.defaultNoteState(),
            unitSize: this.unitSize,
            pinned: this.pinned
        };

        this.updateRectIfNeeded = this.updateRectIfNeeded.bind(this);

        this.onNoteLongTouched = this.onNoteLongTouched.bind(this);
        this.onNoteRectChanged = this.onNoteRectChanged.bind(this);
        this.onNoteRemoved = this.onNoteRemoved.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);

        this.updateIsOpenState = this.updateIsOpenState.bind(this);

        this.onNoteStateChanged = this.onNoteStateChanged.bind(this);

        this.onPinnedChanged = this.onPinnedChanged.bind(this);

        this.onResize = this.onResize.bind(this);
        this.onUnitSizeResize = this.onUnitSizeResize.bind(this);

        this.onContentRef = this.onContentRef.bind(this);
        this.onToolbarRef = this.onToolbarRef.bind(this);

        this.renderAdditionalButtons = this.renderAdditionalButtons.bind(this);

        this.onToggle = this.onToggle.bind(this);
        this.onStyleTransferToAllBtnPressed = this.onStyleTransferToAllBtnPressed.bind(this);
        this.onAlwaysVisibleBtnPressed = this.onAlwaysVisibleBtnPressed.bind(this);
        this.onDumpBtnPressed = this.onDumpBtnPressed.bind(this);
    }

    componentDidMount() {
        this.mounted = true;
    }

    componentWillUnmount()  {
        if(this._resizeTimer)   clearTimeout(this._resizeTimer);
        this._resizeTimer = null;
        this.mounted = false;
    }

    forceUpdatePopovers()   {
        if(this.toolbarRef) this.toolbarRef.forceUpdatePopovers();
    }
    
    componentDidUpdate(prevProps, prevState)    {
        if(!OEToolbox.shallowEqual(this.state.rect, prevState.rect))    {
            this.forceUpdatePopovers();
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.noteLongTouched, this.onNoteLongTouched);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.notesEnabledChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.notesEditModeChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.noteRectChanged, this.onNoteRectChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.noteRemoved, this.onNoteRemoved);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.noteLongTouched, this.onNoteLongTouched);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.notesEnabledChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.notesEditModeChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.noteRectChanged, this.onNoteRectChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.noteRemoved, this.onNoteRemoved);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onOpenStateChanged()	{
        if(!this.oe.isReady()) return;
        let controller = this.oe.sharedInterface.getUIControllerModelState();
        controller.setUpdateEnabled(!this.isOpen);

        let userInfo = new this.oe.Module.Dictionary();
        this.oe.sharedInterface.postNotification(this.oe.NotificationName.uiInstanceNotePopoverOpenStateChanged, userInfo.setBoolValue('open', this.isOpen));
        userInfo.delete();
    }

    close()	{
        if(!this.isOpen) return;
        this.isOpen = false;
        this.setState({isOpen: false});

        if(this.toolbarRef) this.toolbarRef.closeViews();

        this.updateRectIfNeeded();

        this.onOpenStateChanged();
    }

    open(rect)	{
        if(!this.canOpen()) return;

        if(this.isOpen)	{
            if(!rect || OEToolbox.shallowEqual(rect, this.rect)) return;
            this.rect = rect;
            this.setState({rect: rect});
            return;
        }

        this.isOpen = true;
        if(rect)	{
            this.rect = rect;
            this.setState({isOpen: true, rect: this.rect});
        } else {
            this.setState({isOpen: true});
        }
        
        this.onOpenStateChanged();
    }

    canOpen()	{
        if(!this.oe.isReady()) return false;
        let controller = this.oe.sharedInterface.getUIControllerNote();
        return controller.getUIEnabled() && controller.getEnabled() && controller.isEditingEnabled();
    }

    setRect(rect){
        if(OEToolbox.shallowEqual(rect, this.rect)) return;
        this.rect = rect;
        this.setState({rect: this.rect});
    }

    updateId(newId) {
        this.id = newId;
        this.setState({id: newId});
    }

    updateIsOpenState()   {
        if(!this.canOpen())	{
            this.close();
        }
    }

    updateRect()    {
        this.needsRectUpdate = false;
        if(!this.oe.isReady() || this.id === null) return;	// it is not save to call UIControllerNote methods with null for id

        let noteRect = this.oe.sharedInterface.getUIControllerNote().getNoteRect(this.id);
        let rect = {x: noteRect.x, y: noteRect.y, w: noteRect.z, h: noteRect.w};
        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        rect = {x: rect.x * devicePR, y: rect.y * devicePR, w: rect.w * devicePR, h: rect.h * devicePR};

        this.setRect(rect);
    }

    updateRectIfNeeded()    {
        if(!this.needsRectUpdate)   return;
        this.updateRect();
    }

    updateSizeConstrains()  {
        let sizeConstraints;
        if(this.contentRef) {
            let rect = this.contentRef.getBoundingClientRect();
            let windowSize = {w: window.innerWidth || document.documentElement.clientWidth, h: window.innerHeight || document.documentElement.clientHeight};
            let pad = this.pinned ? 0.25 * this.unitSize + 1 : 2;

            sizeConstraints = {w: windowSize.w - rect.left - pad, h: windowSize.h - rect.top - pad};

            let boundariesElement = this.props.boundariesElement;
            if(typeof(boundariesElement) === 'object' && boundariesElement.getBoundingClientRect()) {
                let rect_ = boundariesElement.getBoundingClientRect();
            
                sizeConstraints = {w: Math.min(sizeConstraints.w, rect_.right - rect.left - pad), h: Math.min(sizeConstraints.h, rect_.bottom - rect.top - pad)};
            }
        }

        if(!OEToolbox.shallowEqual(this.sizeConstraints, sizeConstraints)) {
            this.sizeConstraints = sizeConstraints;
            this.setState({sizeConstraints: sizeConstraints});
        }
    }

    updateState(released)   {
        retardUpdate(this, () => {
            if(!this.oe.isReady() || released === true)   {
                this.id = null;
                this.rect = null;
                this.setState({id: this.id, rect: this.rect});
                this.close();
                this.needsRectUpdate = false;
                return;
            }

            this.updateIsOpenState();
        });
    }

    onNoteLongTouched(message, userInfo) {
        if(!this.canOpen()) return;

        this.updateId(userInfo.ID);

        let rect = {x: userInfo.rect.x, y: userInfo.rect.y, w: userInfo.rect.z, h: userInfo.rect.w};
        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        rect = {x: rect.x * devicePR, y: rect.y * devicePR, w: rect.w * devicePR, h: rect.h * devicePR};

        this.open(rect);
    }

    onNoteRectChanged(message, userInfo) {
        if(userInfo.ID !== this.id)  return;

        if(this.toolbarRef && this.toolbarRef.state.isStyleExtPopoverOpen)    {
            this.needsRectUpdate = true;
            return;
        }

        this.updateRect();
    }

    onNoteRemoved() {
        if(this.id === null) return;
        this.close();
    }

    onUIControllerStateChanged(message, userInfo)    {
        if(userInfo.type === this.oe.Module.UIControllerType.note) {
            this.updateIsOpenState();
        }
    }

    onNoteStateChanged(sender, noteState)   {
        this.setState({noteState: clone(noteState)});
    }
    
    onPinnedChanged(sender, pinned) {
        this.pinned = pinned;
        this.setState({pinned: pinned});
        this.updateSizeConstrains();
    }

    onResize(sender, size)  {
        if(!this.mounted || size.w == 0 || size.h == 0 || OEToolbox.shallowEqual(this.size, size))   return;

        this.size = size;
        this.updateSizeConstrains();

        if(this._resizeTimer)   clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => { 
            this._resizeTimer = null;
            this.setState({size: size});                // setting size state deferred prevents "ResizeObserver loop completed with undelivered notifications." error
        }, 333);

        this.forceUpdate();
        this.forceUpdatePopovers();
    }

    onUnitSizeResize(sender, size)  {
        if(this.unitSize == size.w) return;
        this.unitSize = size.w;
        this.setState({unitSize: size.w});
        this.updateSizeConstrains();
    }

    onContentRef(ref)   {
        this.contentRef = ref;
        this.updateSizeConstrains();
    }

    onToolbarRef(ref)   {
        this.toolbarRef = ref;
    }

    renderAdditionalButtons(props)  {
        return (
            <React.Fragment>
                {
                /*
                    props.devFeatures ? <OEIconButton className={props.buttonClassName + ' style-transfer-to-all-btn'} iconClassName="rotate-180" icon={OEIconCodes.noteStyleTransferToAll} onPressed={this.onStyleTransferToAllBtnPressed}/> : null
                */
                }

                {props.devFeatures ? <OEIconButton className={props.buttonClassName + ' always-visible-btn'} icon={props.showAlways ? OEIconCodes.noteShowAlways : OEIconCodes.noteNotShowAlways} onPressed={this.onAlwaysVisibleBtnPressed}/> : null}

                <OEIconButton className={props.buttonClassName + ' dump-btn'} icon={OEIconCodes.noteDump} onPressed={this.onDumpBtnPressed}/>
            </React.Fragment>
        );
    }

    render() {
        let yOffset = 2.625 - (this.state.pinned ? this.props.offset.y / this.state.unitSize : 0);

        let style = {maxWidth: 'calc(100vw - 1em)', maxHeight: 'calc(100vh - ' + yOffset + 'em)'};

        if(this.props.resizeable)   {
            style.resize = 'both';

            if(this.state.size) {
                style.width = this.state.size.w;
                style.height = this.state.size.h;
            }
    
            if(this.state.sizeConstraints)  {
                style.maxWidth = 'min(' + this.state.sizeConstraints.w / this.state.unitSize + 'em, ' + style.maxWidth + ')'
                style.maxHeight = 'min(' + this.state.sizeConstraints.h / this.state.unitSize + 'em, ' + style.maxHeight + ')'
            }
        }

        //
        const rect = !this.state.rect ? null : {x: this.state.rect.x + this.props.offset.x, y: this.state.rect.y + this.props.offset.y, w: this.state.rect.w, h: this.state.rect.h};

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <OENoteStateProvider moduleId={this.props.moduleId} id={this.state.id} onNoteStateChanged={this.onNoteStateChanged}/>

                <OEPopover
                    moduleId={this.props.moduleId}
                    className="popover-control"
                    boundariesElement={this.props.boundariesElement}
                    placement="bottom"
                    rect={rect}
                    buttonClassName="transparent-btn"
                    headerSeparator={false}
                    additionalButtons={this.renderAdditionalButtons}
                    isOpen={this.state.isOpen && this.state.rect !== null}
                    onToggle={this.onToggle}
                    popoverTargetId={this.popoverTargetId}
                    titleId="note_popover"
                    unpinnable={this.props.unpinnable}
                    backdrop={true}
                    backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                    devFeatures={this.props.devFeatures}
                    showAlways={this.state.noteState.style.showAlways}
                    onPinnedChanged={this.onPinnedChanged}
                >

                    <div className="note-popover" style={style} ref={this.onContentRef}>
                        {this.props.resizeable ? <OEResizeObserver onResize={this.onResize}/> : null}

                        <OEResizeObserver className="unit-size-observer" onResize={this.onUnitSizeResize}/>

                        <OENoteTextEdit moduleId={this.props.moduleId} id={this.state.id}/>
                        
                        <OENoteToolbar
                            appComponent={this.props.appComponent}
                            moduleId={this.props.moduleId}
                            id={this.state.id}
                            boundariesElement={this.props.boundariesElement}
                            popoverTargetId={this.popoverTargetId}
                            devFeatures={this.props.devFeatures}
                            updateRectIfNeeded={this.updateRectIfNeeded}
                            ref={this.onToolbarRef}
                        />
                    </div>

                </OEPopover>
            </React.Fragment>
        );
    }
    
    onToggle()  {
        this.close();
    }

    onStyleTransferToAllBtnPressed()	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().transferNoteStyleToAll(this.id);
    }
    
    onAlwaysVisibleBtnPressed()	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().setNoteShowAlways(this.id, !this.state.noteState.style.showAlways);
    }

    onDumpBtnPressed()	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().removeNote(this.id);
    }
}

OENotePopoverController.defaultProps = {
    moduleId:               '',
    devFeatures:            false,
    unpinnable:             false,
    resizeable:             false
};

OENotePopoverController.propTypes = {
    moduleId:               PropTypes.string,
    devFeatures:            PropTypes.bool,
    unpinnable:             PropTypes.bool,
    resizeable:             PropTypes.bool
};

export default frefFromRef(connectAppEnv((env) => {
    let devFeatures = env.config.module.flags && env.config.module.flags.includes(AppFlags.import)
    return {
        appComponent:       env.component,
        devFeatures:        devFeatures,
        unpinnable:         devFeatures,
        resizeable:         devFeatures
    };
})(connectModuleEnv((env) => {
    return {
        offset: {x: 0, y: -env.ui.capBar.edgeOffsets.top}
    };
})(frefToRef(OENotePopoverController))));